import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { regions } from "@/lib/data/regions";
import useSEO from "@/hooks/useSEO";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";

export default function Regions() {
  useSEO({
    title: "Regions — Manitoba Coverage | Manitoba Security Services",
    description:
      "Coverage across every region of Manitoba — Capital Region, Westman, Northern, Interlake, Southeast, Pembina Valley, Central Plains, Parkland, Eastman, and Red River.",
    keywords: withCanadaKeywords([
      "Manitoba regions security",
      "Manitoba security coverage map",
      ...regions.flatMap((r) => r.keywords).slice(0, 20),
    ]),
    canonical:
      typeof window !== "undefined" ? window.location.origin + "/regions" : "/regions",
  });

  return (
    <div className="bg-[#050505] pt-40 md:pt-48">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">Index — 09</p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1
            data-testid="regions-title"
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
          >
            Regions{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              of Manitoba
            </span>
          </h1>
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Ten operating regions across the province. Each with a dedicated
            dispatch approach, region-specific officer profiles, and coverage
            calibrated to the local economy.
          </p>
        </div>
      </section>

      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          {regions.map((r, i) => (
            <motion.div
              key={r.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/regions/${r.slug}`}
                data-testid={`region-${r.slug}`}
                className="group grid grid-cols-12 gap-4 items-baseline border-b border-[#1e1e1e] py-8 md:py-10 hover:border-[#FF3B30] transition-colors duration-500"
              >
                <span className="col-span-2 md:col-span-1 font-editorial italic text-[#6a6a6a] group-hover:text-[#FF3B30] transition-colors text-lg md:text-2xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="col-span-8 md:col-span-9">
                  <h2 className="font-display font-black uppercase tracking-tight text-white text-2xl md:text-4xl lg:text-5xl leading-none group-hover:translate-x-2 transition-transform duration-500">
                    {r.name}
                  </h2>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-base md:text-lg max-w-2xl">
                    {r.tag}
                  </p>
                </div>
                <span className="col-span-2 flex justify-end">
                  <ArrowUpRight
                    className="text-white/60 group-hover:text-[#FF3B30] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    size={22}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
